/**
 * Password strength scorer (web copy).
 *
 * Scoring rules (mirror these in `src/components/auth/PasswordStrength.tsx`
 * on the mobile side):
 *   base    = length × 4
 *   bonuses = +10 digit, +10 symbol, +10 mixed-case, +5 len≥12, +10 len≥16
 *   penalty = −20 if matches a common-list keyword
 *
 * Bucket boundaries:
 *   ≤ 20 → 'veryWeak'   (1 bar, red)
 *   21–40 → 'weak'      (2 bars, red)
 *   41–60 → 'fair'      (3 bars, amber)
 *   61–80 → 'good'      (4 bars, amber)
 *   > 80 → 'strong'     (5 bars, green)
 */
export type StrengthLevel = 'veryWeak' | 'weak' | 'fair' | 'good' | 'strong';

const COMMON_KEYWORDS = ['password', '12345678', 'qwerty', 'letmein', 'admin', 'welcome'];

export function scorePassword(password: string): number {
  if (!password) return 0;

  let score = password.length * 4;

  if (/\d/.test(password)) score += 10;
  if (/[^A-Za-z0-9]/.test(password)) score += 10;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 10;
  if (password.length >= 12) score += 5;
  if (password.length >= 16) score += 10;

  const lower = password.toLowerCase();
  if (COMMON_KEYWORDS.some((keyword) => lower.includes(keyword))) score -= 20;

  return Math.max(0, Math.min(100, score));
}

export function getStrengthLevel(score: number): StrengthLevel {
  if (score <= 20) return 'veryWeak';
  if (score <= 40) return 'weak';
  if (score <= 60) return 'fair';
  if (score <= 80) return 'good';
  return 'strong';
}

export const STRENGTH_LABELS: Record<StrengthLevel, string> = {
  veryWeak: 'Very weak',
  weak: 'Weak',
  fair: 'Fair',
  good: 'Good',
  strong: 'Strong',
};

export const STRENGTH_FILLED_BARS: Record<StrengthLevel, number> = {
  veryWeak: 1,
  weak: 2,
  fair: 3,
  good: 4,
  strong: 5,
};

export interface PasswordRequirements {
  length: boolean;
  number: boolean;
  mixedCase: boolean;
  symbol: boolean;
}

export function checkRequirements(password: string): PasswordRequirements {
  return {
    length: password.length >= 8,
    number: /\d/.test(password),
    mixedCase: /[a-z]/.test(password) && /[A-Z]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
}
